import { useState, useCallback, useEffect, useRef, lazy, Suspense } from "react";
import { supabase } from './lib/supabase';
import { auth } from './lib/firebase';
import { onIdTokenChanged } from 'firebase/auth';
import { resolvePracticeId } from './lib/practiceQuery';
// xlsx (~1MB) is loaded on demand inside exportToXLSX — keep it out of the main bundle.
import TranscribeVisit from './TranscribeVisit';
import { ORTHO_ICD10, ORTHO_CPT } from './data/orthoData';
import { claudePost, parseJSON } from './utils/claudeApi';
import { fmt, duration } from './utils/formatting';
import { todayISO } from './utils/chartHelpers';
import CalendarNav from './components/CalendarNav';
import EventRow from './components/EventRow';
import SettingsModal from './components/SettingsModal';
import CalendarModal from './components/CalendarModal';
import CalendarPickerModal from './components/CalendarPickerModal';
import SavedNotesPanel from './components/SavedNotesPanel';
import EMRShell from './components/EMRShell';
import SubscriptionGate from './components/SubscriptionGate';
const PortalApp = lazy(() => import('./components/PortalApp')); // patients-only route — keep out of provider bundle
const ProviderLogin  = lazy(() => import('./components/ProviderLogin'));
const OnboardingPage = lazy(() => import('./components/OnboardingPage'));
const MFAVerify      = lazy(() => import('./components/MFAVerify'));
const PrivacyNoticePage = lazy(() => import('./pages/PrivacyNoticePage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
// Scribe tier (Batch 3) — public signup + checkout return + signed-in landing.
const ScribeSignupPage  = lazy(() => import('./pages/ScribeSignupPage'));
const ScribeWelcomePage = lazy(() => import('./pages/ScribeWelcomePage'));
const ScribeAccountPage = lazy(() => import('./pages/ScribeAccountPage'));

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';
// localStorage demo flag is only honoured on non-production domains.
// On epiphanymd.com it is always ignored, preventing stale flags from
// a prior demo session bypassing the login screen.
const localDemoEnabled = () =>
  localStorage.getItem('epiphany_demo_mode') === 'true' &&
  window.location.hostname !== 'epiphanymd.com';

// ── MFA "Enable 2FA" nudge banner ────────────────────────────────────────────
function MfaBanner({ onDismiss }) {
  return (
    <div style={{
      position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)',
      zIndex: 2000, background: '#1a2535', borderRadius: 10,
      border: '1px solid rgba(240,165,0,0.45)', padding: '10px 16px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      maxWidth: 'calc(100vw - 40px)', whiteSpace: 'nowrap',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
      <span style={{ fontSize: 13, color: '#f0a500', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Secure your account — enable two-factor authentication in Settings → Security
      </span>
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', color: '#546e7a',
        cursor: 'pointer', fontSize: 18, padding: '0 2px', lineHeight: 1, flexShrink: 0,
      }}>✕</button>
    </div>
  );
}

const AppLoader = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f0f23', color: '#64748b', fontSize: 14, fontFamily: 'system-ui' }}>Loading…</div>;

// Top 50 most common codes — keeps single-event prompts small
const TOP_ICD10 = ORTHO_ICD10.slice(0, 50);
const TOP_CPT = ORTHO_CPT.slice(0, 50);

function parseTokenFromUrl() {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  return params.get("access_token") || null;
}

async function fetchCalendarList(accessToken) {
  const res = await fetch("https://www.googleapis.com/calendar/v3/users/me/calendarList", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`CalendarList API error: ${res.status}`);
  const data = await res.json();
  return (data.items || []).map(cal => ({
    id: cal.id,
    name: cal.summary || cal.id,
    color: cal.backgroundColor || "#6366f1",
    primary: cal.primary || false,
  }));
}

async function fetchGoogleCalendarEvents(accessToken, weekStart, calendarIds = ["primary"]) {
  const startOfWeek = new Date(weekStart);
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000);

  const allEvents = await Promise.all(calendarIds.map(async (calId) => {
    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calId)}/events?timeMin=${startOfWeek.toISOString()}&timeMax=${endOfWeek.toISOString()}&singleEvents=true&orderBy=startTime&maxResults=50`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.items || []).map((item, i) => ({
      id: item.id || `${calId}-${i}`,
      title: item.summary || "Untitled",
      start: item.start?.dateTime || item.start?.date || new Date().toISOString(),
      end: item.end?.dateTime || item.end?.date || new Date().toISOString(),
      location: item.location || "",
      notes: item.description || "",
      calendarName: calId,
      dob: "",
      icd10: [],
      cpt: [],
    }));
  }));

  return allEvents.flat().sort((a, b) => new Date(a.start) - new Date(b.start));
}

function buildCodeLists() {
  return `ICD-10 options: ${TOP_ICD10.map(x => x.code + " - " + x.desc).join("; ")}\nCPT options: ${TOP_CPT.map(x => x.code + " - " + x.desc).join("; ")}`;
}

async function suggestCodes(event) {
  const prompt = `You are an orthopedic surgery billing expert. For the following calendar event, pick the TOP 2 ICD-10 codes and TOP 2 CPT codes. Return ONLY a JSON object with keys "icd10" and "cpt" (arrays of code strings). No explanation, no markdown.

Event Title: ${event.title}
Location: ${event.location || "N/A"}
Notes: ${event.notes || "N/A"}

${buildCodeLists()}`;
  const text = await claudePost([{ role: "user", content: prompt }], 300);
  return parseJSON(text, { icd10: [], cpt: [] });
}

async function suggestAllBatch(events) {
  const eventLines = events.map((e, i) =>
    `Event ${i + 1}: Title: ${e.title} | Location: ${e.location || "N/A"} | Notes: ${e.notes || "N/A"}`
  ).join("\n");

  const prompt = `You are an orthopedic surgery billing expert. For each of the ${events.length} calendar events below, pick the 2 most appropriate ICD-10 codes and 2 CPT codes from the provided lists.

${eventLines}

${buildCodeLists()}

You MUST return a JSON array with exactly one object per event, in the same order. The array must have exactly ${events.length} items. Do not skip any events.
Each object must have keys "icd10" and "cpt" (arrays of 2 code strings). No explanation, no markdown, no backticks.`;

  const text = await claudePost([{ role: "user", content: prompt }], 4000);
  const results = parseJSON(text, []);
  if (!Array.isArray(results)) {
    console.error("Unexpected batch response shape:", text);
    throw new Error("AI response was not a JSON array");
  }
  while (results.length < events.length) {
    results.push({ icd10: [], cpt: [] });
  }
  return results;
}

async function exportToXLSX(events) {
  const XLSX = await import("xlsx"); // code-split: loaded only when exporting
  const rows = events.map(e => ({
    "Patient / Event Title": e.title,
    "Date & Time": fmt(e.start),
    "Duration": duration(e.start, e.end),
    "Date of Birth": e.dob || "",
    "Location": e.location || "",
    "Notes": e.notes || "",
    "ICD-10 Codes": (e.icd10 || []).join(", "),
    "ICD-10 Descriptions": (e.icd10 || []).map(c => ORTHO_ICD10.find(x => x.code === c)?.desc || c).join("; "),
    "CPT Codes": (e.cpt || []).join(", "),
    "CPT Descriptions": (e.cpt || []).map(c => ORTHO_CPT.find(x => x.code === c)?.desc || c).join("; "),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [30,22,10,14,20,40,20,50,16,50].map(w => ({ wch: w }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Ortho Billing");
  XLSX.writeFile(wb, `ortho_billing_${todayISO()}.xlsx`);
}

function exportToGSheets(events) {
  const headers = ["Patient/Event","Date & Time","Duration","DOB","Location","Notes","ICD-10 Codes","ICD-10 Descriptions","CPT Codes","CPT Descriptions"];
  const rows = events.map(e => [
    e.title, fmt(e.start), duration(e.start, e.end), e.dob || "", e.location || "", e.notes || "",
    (e.icd10 || []).join(", "),
    (e.icd10 || []).map(c => ORTHO_ICD10.find(x => x.code === c)?.desc || c).join("; "),
    (e.cpt || []).join(", "),
    (e.cpt || []).map(c => ORTHO_CPT.find(x => x.code === c)?.desc || c).join("; "),
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ortho_billing_${todayISO()}.csv`;
  a.click();
  setTimeout(() => window.open("https://sheets.new", "_blank"), 600);
}

function demoEvents() {
  const base = new Date();
  return [
    { id: 1, title: "Pre-op: Total Knee Replacement - James Holloway", start: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 9, 0).toISOString(), end: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 9, 30).toISOString(), location: "OR Suite 3", notes: "Right knee, severe OA, patient has diabetes", dob: "1958-03-14", icd10: [], cpt: [] },
    { id: 2, title: "Post-op Follow-up: Hip Arthroplasty - Sandra Chen", start: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 10, 30).toISOString(), end: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 11, 0).toISOString(), location: "Clinic Room 4", notes: "6-week post-op left hip replacement, checking wound healing", dob: "1964-07-22", icd10: [], cpt: [] },
    { id: 3, title: "New Patient: Rotator Cuff Tear - Marcus Webb", start: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 13, 0).toISOString(), end: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 13, 45).toISOString(), location: "Exam Room 2", notes: "Right shoulder pain x 3 months, MRI confirms partial rotator cuff tear", dob: "1979-11-05", icd10: [], cpt: [] },
    { id: 4, title: "Lumbar Spine Consultation - Patricia Rios", start: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 14, 30).toISOString(), end: new Date(base.getFullYear(), base.getMonth(), base.getDate(), 15, 15).toISOString(), location: "Spine Clinic", notes: "L4-L5 disc herniation, radiating pain to left leg, considering microdiscectomy", dob: "1971-09-30", icd10: [], cpt: [] },
  ];
}

function mergeWithSavedCodes(events) {
  try {
    const saved = JSON.parse(localStorage.getItem("ortho_codes") || "{}");
    return events.map(e => saved[e.id] ? { ...e, icd10: saved[e.id].icd10, cpt: saved[e.id].cpt } : e);
  } catch { return events; }
}

// ── Auth-aware routing wrapper ────────────────────────────────────────────────
function ProviderAppWithAuth() {
  const [session,       setSession]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [showMfaBanner, setShowMfaBanner] = useState(false);

  useEffect(() => {
    // env-var demo mode: skip auth entirely (no real session needed).
    if (DEMO_MODE) { setLoading(false); return; }

    // §5 Auth — Identity Platform. onIdTokenChanged fires on sign-in, sign-out,
    // and token refresh. MFA is enforced at sign-in (ProviderLogin resolver), so
    // there is no post-login AAL gate here.
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) { setSession(null); setLoading(false); return; }

      // Custom claims (practice_id) + the ID token.
      let claims = {};
      try { claims = (await user.getIdTokenResult()).claims || {}; } catch { /* ignore */ }
      const token = await user.getIdToken().catch(() => null);

      // Session adapter — keep the supabase-shaped object the app already reads
      // (session.user.id / .email / .app_metadata.practice_id) so the ~36
      // downstream consumers are unchanged. account_type (scribe tier, Batch 3)
      // is additive — only the scribe branch below reads it.
      setSession({
        user: { id: user.uid, email: user.email, app_metadata: { practice_id: claims.practice_id, account_type: claims.account_type } },
        access_token: token,
      });
      setLoading(false);

      // Nudge to enroll a second factor if none enrolled (dismissible 7 days).
      try {
        const enrolled = (user.multiFactor && user.multiFactor.enrolledFactors) || [];
        if (enrolled.length === 0) {
          const ts = parseInt(localStorage.getItem('mfa_banner_dismissed') || '0', 10);
          if (Date.now() - ts > 7 * 24 * 60 * 60 * 1000) setShowMfaBanner(true);
        }
      } catch { /* ignore */ }

      // Authenticated but no practice_id → onboarding not complete.
      if (!claims.practice_id && window.location.pathname !== '/onboarding') {
        window.location.pathname = '/onboarding';
      }
    });
    return () => unsub();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#070d14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#64748b', fontSize: 14, fontFamily: 'system-ui' }}>Loading…</div>
      </div>
    );
  }

  if (!session && !DEMO_MODE) return <Suspense fallback={AppLoader}><ProviderLogin /></Suspense>;

  // Authenticated but no practice_id → onboarding not complete
  const isDemo = DEMO_MODE;
  const hasPractice = !!session?.user?.app_metadata?.practice_id;
  if (!isDemo && !hasPractice) {
    return <Suspense fallback={AppLoader}><OnboardingPage /></Suspense>;
  }

  // Scribe-tier accounts never see the EMR shell: the phone app is the
  // product; the web is status + billing + download. Server-side the
  // account_type claim already 403s every PHI query — this branch replaces
  // the 403-walled EMR chrome with a page built for what scribe CAN do.
  if (!isDemo && session?.user?.app_metadata?.account_type === 'scribe') {
    return <Suspense fallback={AppLoader}><ScribeAccountPage session={session} /></Suspense>;
  }

  return (
    <>
      {showMfaBanner && (
        <MfaBanner onDismiss={() => {
          setShowMfaBanner(false);
          localStorage.setItem('mfa_banner_dismissed', String(Date.now()));
        }} />
      )}
      {!isDemo && <SubscriptionGate practiceId={session.user.app_metadata.practice_id} />}
      <ProviderMain session={session} practiceId={isDemo ? '00000000-0000-0000-0000-000000000001' : session.user.app_metadata.practice_id} />
    </>
  );
}

// ── Main app export ────────────────────────────────────────────────────────────
export default function App() {
  const path = window.location.pathname;
  if (path.startsWith('/portal')) return <Suspense fallback={AppLoader}><PortalApp /></Suspense>;
  if (path === '/onboarding') return <Suspense fallback={AppLoader}><OnboardingPage /></Suspense>;
  if (path === '/reset-password') return <Suspense fallback={AppLoader}><ProviderLogin /></Suspense>;
  if (path === '/privacy-notice') return <Suspense fallback={AppLoader}><PrivacyNoticePage /></Suspense>;
  if (path === '/privacy-policy') return <Suspense fallback={AppLoader}><PrivacyNoticePage /></Suspense>;
  if (path === '/features') return <Suspense fallback={AppLoader}><FeaturesPage /></Suspense>;
  if (path === '/terms') return <Suspense fallback={AppLoader}><TermsPage /></Suspense>;
  // Scribe signup is prod-only: both hosting targets ship this same bundle,
  // and a demo-site signup would provision REAL accounts/practices.
  if (!DEMO_MODE && path === '/scribe') return <Suspense fallback={AppLoader}><ScribeSignupPage /></Suspense>;
  if (!DEMO_MODE && path === '/scribe/welcome') return <Suspense fallback={AppLoader}><ScribeWelcomePage /></Suspense>;
  return <ProviderAppWithAuth />;
}

// ── Provider main content (all calendar/EMR logic) ────────────────────────────
function ProviderMain({ session, practiceId }) {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [connected, setConnected] = useState(null);
  const [suggestingAll, setSuggestingAll] = useState(false);
  const [notification, setNotification] = useState(null);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("ortho_api_key") || "");
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [weekStart, setWeekStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [calToken, setCalToken] = useState(() => localStorage.getItem("ortho_cal_token") || null);
  const [calendarList, setCalendarList] = useState([]);
  const [selectedCalendars, setSelectedCalendars] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ortho_selected_cals") || "null") || null; } catch { return null; }
  });
  const [showCalPicker, setShowCalPicker] = useState(false);
  const [savedNotes, setSavedNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ortho_saved_notes") || "[]"); } catch { return []; }
  });

  const notify = useCallback((msg, color = "#4ade80", isSave = false) => {
    const isDemoMode = localDemoEnabled() || process.env.REACT_APP_DEMO_MODE === 'true';
    const displayMsg = (isDemoMode && isSave) ? "Demo mode — this would be saved in a live account" : msg;
    setNotification({ msg: displayMsg, color });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  // On mount: check for OAuth token in URL or localStorage
  useEffect(() => {
    const urlToken = parseTokenFromUrl();
    const savedToken = localStorage.getItem("ortho_cal_token");
    const token = urlToken || savedToken;
    if (!token) return;
    if (urlToken) window.history.replaceState(null, "", window.location.pathname);
    setCalToken(token);
    localStorage.setItem("ortho_cal_token", token);
    setLoadingCalendar(true);

    fetchCalendarList(token)
      .then(cals => {
        setCalendarList(cals);
        const saved = (() => { try { return JSON.parse(localStorage.getItem("ortho_selected_cals")); } catch { return null; } })();
        const ids = saved || cals.filter(c => c.primary).map(c => c.id);
        setSelectedCalendars(ids);
        return fetchGoogleCalendarEvents(token, new Date(), ids);
      })
      .then(calEvents => {
        setEvents(mergeWithSavedCodes(calEvents));
        setConnected("google");
        setLoadingCalendar(false);
        notify(`Google Calendar loaded! ${calEvents.length} events.`);
      })
      .catch(err => {
        console.error(err);
        localStorage.removeItem("ortho_cal_token");
        setCalToken(null);
        setLoadingCalendar(false);
        notify("Session expired — please reconnect your calendar.", "#f87171");
      });
  }, [notify]);

  const navigateTo = (newStart) => {
    if (!calToken) return;
    setWeekStart(newStart);
    setLoadingCalendar(true);
    fetchGoogleCalendarEvents(calToken, newStart, selectedCalendars || ["primary"])
      .then(calEvents => {
        setEvents(mergeWithSavedCodes(calEvents));
        setLoadingCalendar(false);
        notify(`Loaded ${calEvents.length} events`);
      })
      .catch(err => {
        console.error(err);
        setLoadingCalendar(false);
        notify("Failed to load events.", "#f87171");
      });
  };

  const applyCalendarSelection = (ids) => {
    setSelectedCalendars(ids);
    localStorage.setItem("ortho_selected_cals", JSON.stringify(ids));
    setLoadingCalendar(true);
    fetchGoogleCalendarEvents(calToken, weekStart, ids)
      .then(calEvents => {
        setEvents(mergeWithSavedCodes(calEvents));
        setLoadingCalendar(false);
        notify(`Loaded ${calEvents.length} events from ${ids.length} calendar${ids.length !== 1 ? "s" : ""}`);
      })
      .catch(() => { setLoadingCalendar(false); notify("Failed to load events.", "#f87171"); });
  };

  const weekLabel = () => {
    const start = new Date(weekStart);
    const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
    const f = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${f(start)} – ${f(end)}, ${start.getFullYear()}`;
  };

  const saveApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem("ortho_api_key", key);
    notify("API key saved!", "#4ade80", true);
  };

  const loadDemo = () => {
    setEvents(demoEvents());
    setConnected("demo");
    notify("Demo events loaded! Try '✨ AI Suggest All'");
  };

  const disconnect = () => {
    localStorage.removeItem("ortho_cal_token");
    localStorage.removeItem("ortho_selected_cals");
    setCalToken(null);
    setConnected(null);
    setEvents([]);
    setCalendarList([]);
    setSelectedCalendars(null);
  };

  const updateEvent = useCallback((updated) => {
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
  }, []);

  const deleteEvent = useCallback((id) => {
    setEvents(prev => prev.filter(e => e.id !== id));
  }, []);

  const saveNote = useCallback((note) => {
    const existing = localStorage.getItem("ortho_saved_notes") || "[]";
    const pct = (new Blob([existing]).size / (5 * 1024 * 1024)) * 100;
    if (pct > 95) {
      notify("Storage full — export and clear old notes before saving new ones.", "#f87171");
      return;
    }
    setSavedNotes(prev => {
      const next = [note, ...prev];
      localStorage.setItem("ortho_saved_notes", JSON.stringify(next));
      return next;
    });
    notify("Visit note saved! 📋", "#4ade80", true);
  }, [notify]);

  const clearAllNotes = useCallback(() => {
    setSavedNotes([]);
    localStorage.setItem("ortho_saved_notes", JSON.stringify([]));
    notify("All notes cleared.");
  }, [notify]);

  const deleteNote = useCallback((id) => {
    setSavedNotes(prev => {
      const next = prev.filter(n => n.id !== id);
      localStorage.setItem("ortho_saved_notes", JSON.stringify(next));
      return next;
    });
  }, []);

  const renameNote = useCallback((id, name) => {
    setSavedNotes(prev => {
      const next = prev.map(n => n.id === id ? { ...n, patientName: name } : n);
      localStorage.setItem("ortho_saved_notes", JSON.stringify(next));
      return next;
    });
  }, []);

  const exportNotes = () => {
    if (!savedNotes.length) { notify("No saved notes to export.", "#f87171"); return; }
    const divider = "═".repeat(70);
    const thin = "─".repeat(70);
    const lines = savedNotes.map(n => [
      divider,
      [n.patientName ? `Patient: ${n.patientName}` : null, `Date: ${n.dateTime}`, n.visitType ? `Visit: ${n.visitType}` : null].filter(Boolean).join("   |   "),
      thin,
      "CLINIC NOTE", n.note || "",
      "", "COUNSELING SUMMARY", n.counseling || "N/A",
      "", "TREATMENT PLAN", n.treatmentPlan || "N/A",
      "", "ICD-10 CODES", ...(n.icd10 || []).map(c => `  ${c.code}  –  ${c.description}`),
      "", "CPT CODES", ...(n.cpt || []).map(c => `  ${c.code}  –  ${c.description}`),
    ].join("\n")).join("\n\n");
    const blob = new Blob([lines], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `epiphanymd-notes-${todayISO()}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  // Auto-persist codes whenever events change
  useEffect(() => {
    if (!events.length) return;
    const existing = (() => { try { return JSON.parse(localStorage.getItem("ortho_codes") || "{}"); } catch { return {}; } })();
    const updates = {};
    events.forEach(e => {
      if ((e.icd10?.length || 0) + (e.cpt?.length || 0) > 0) {
        updates[e.id] = { icd10: e.icd10, cpt: e.cpt };
      }
    });
    localStorage.setItem("ortho_codes", JSON.stringify({ ...existing, ...updates }));
  }, [events]);

  const clearCodes = useCallback((id) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, icd10: [], cpt: [] } : e));
    try {
      const existing = JSON.parse(localStorage.getItem("ortho_codes") || "{}");
      delete existing[id];
      localStorage.setItem("ortho_codes", JSON.stringify(existing));
    } catch {}
  }, []);

  const exportCSV = () => {
    const rows = events.filter(e => (e.icd10?.length || 0) + (e.cpt?.length || 0) > 0);
    if (!rows.length) { notify("No events with codes to export.", "#f87171"); return; }
    const esc = s => `"${String(s).replace(/"/g, '""')}"`;
    const lines = [
      ["Date", "Patient/Event Name", "ICD-10 Codes", "CPT Codes"].map(esc).join(","),
      ...rows.map(e => [esc(fmt(e.start)), esc(e.title), esc((e.icd10 || []).join("; ")), esc((e.cpt || []).join("; "))].join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `epiphanymd-codes-${todayISO()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSuggest = useCallback(async (event) => {
    return await suggestCodes(event);
  }, []);

  const suggestAll = async () => {
    setSuggestingAll(true);
    try {
      const results = await suggestAllBatch(events);
      setEvents(events.map((e, i) => ({ ...e, ...results[i] })));
      notify(`AI suggestions applied to all ${events.length} events!`);
    } catch (err) {
      console.error("Suggest all failed:", err);
      notify("AI suggestions failed — check console for details.", "#f87171");
    }
    setSuggestingAll(false);
  };

  const s = {
    btn: (bg, border, color) => ({ background: bg, border: `1px solid ${border}`, color, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }),
  };

  // ── Calendar & Billing page content ─────────────────────────────────────────
  const calendarPage = (
    <div>
      {/* Action toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 24 }}>
        {connected && (
          <div style={{ fontSize: 11, color: "#4ade80", background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 20, padding: "4px 12px" }}>
            ● {connected === "demo" ? "Demo Mode" : connected === "microsoft" ? "Outlook Connected" : "Google Calendar Connected"}
          </div>
        )}
        {connected === "google" && calendarList.length > 0 && (
          <button onClick={() => setShowCalPicker(true)} style={s.btn("rgba(99,102,241,0.15)", "rgba(99,102,241,0.3)", "#a5b4fc")}>
            📅 {selectedCalendars ? selectedCalendars.length : 1} Calendar{(selectedCalendars?.length || 1) !== 1 ? "s" : ""}
          </button>
        )}
        {connected === "google" && (
          <button onClick={disconnect} style={s.btn("rgba(255,255,255,0.04)", "rgba(255,255,255,0.1)", "rgba(255,255,255,0.4)")}>Disconnect</button>
        )}
        <button onClick={exportCSV} style={s.btn("rgba(52,211,153,0.1)", "rgba(52,211,153,0.25)", "#6ee7b7")}>📊 Export Codes</button>
        <button onClick={() => setShowModal(true)} style={s.btn("rgba(99,102,241,0.25)", "rgba(99,102,241,0.4)", "#c7d2fe")}>🔗 Connect Calendar</button>
      </div>

      {/* Empty state */}
      {events.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 30, fontWeight: 700, margin: "0 0 12px", color: "#f0ede8" }}>Epiphany MD</h1>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, maxWidth: 400, margin: "0 auto 32px", lineHeight: 1.7 }}>Connect your Outlook or Google Calendar to auto-generate ICD-10 and CPT codes for each appointment, then export to Excel or Google Sheets.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setShowModal(true)} style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.8), rgba(16,185,129,0.6))", border: "none", color: "#fff", borderRadius: 10, padding: "12px 24px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>🔗 Connect Calendar</button>
          </div>
        </div>
      )}

      {/* Events list */}
      {events.length > 0 && (
        <>
          <CalendarNav weekStart={weekStart} onNavigate={navigateTo} disabled={!calToken} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, margin: "0 0 4px", fontWeight: 600, color: "#f0ede8" }}>{events.length} Appointment{events.length !== 1 ? "s" : ""}</h2>
              {calToken && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 2 }}>{weekLabel()}</div>}
              {!calToken && <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 13, margin: "4px 0 0" }}>Review events, confirm codes, then export</p>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={suggestAll} disabled={suggestingAll} style={s.btn(suggestingAll ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.35)", "rgba(99,102,241,0.5)", "#c7d2fe")}>
                {suggestingAll ? "✨ Suggesting…" : "✨ AI Suggest All"}
              </button>
              <button onClick={() => exportToXLSX(events)} style={s.btn("rgba(16,185,129,0.2)", "rgba(16,185,129,0.4)", "#4ade80")}>⬇ Excel</button>
              <button onClick={() => exportToGSheets(events)} style={s.btn("rgba(255,255,255,0.06)", "rgba(255,255,255,0.12)", "rgba(255,255,255,0.7)")}>📊 Google Sheets</button>
            </div>
          </div>
        </>
      )}

      {events.map(event => (
        <EventRow key={event.id} event={event} onUpdate={updateEvent} onSuggest={handleSuggest} onDelete={deleteEvent} onClearCodes={clearCodes} />
      ))}

      {events.length > 0 && (
        <button
          onClick={() => setEvents(prev => [...prev, { id: Date.now(), title: "New Appointment", dob: "", start: new Date().toISOString(), end: new Date(Date.now() + 3600000).toISOString(), location: "", notes: "", icd10: [], cpt: [] }])}
          style={{ width: "100%", padding: "14px", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 12, color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 13, marginTop: 4 }}>
          + Add Manual Event
        </button>
      )}

      {events.length > 0 && (
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.2)", fontSize: 11, marginTop: 32 }}>
          ICD-10 &amp; CPT suggestions are AI-generated and should be reviewed by a certified medical coder before submission.
        </p>
      )}
    </div>
  );

  // ── Transcribe Visit page content ────────────────────────────────────────────
  const transcribePage = (
    <>
      <TranscribeVisit onNoteSaved={saveNote} />
      <SavedNotesPanel notes={savedNotes} onDelete={deleteNote} onExport={exportNotes} onRename={renameNote} onClearAll={clearAllNotes} />
    </>
  );

  return (
    <>
      {/* Fixed overlays — rendered outside EMRShell, work as position:fixed */}
      {notification && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 400, background: "#0f1923", border: `1px solid ${notification.color}40`, borderLeft: `3px solid ${notification.color}`, borderRadius: 10, padding: "12px 20px", fontSize: 13, color: notification.color }}>
          {notification.msg}
        </div>
      )}

      {loadingCalendar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 350 }}>
          <div style={{ background: "#0f1923", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, padding: "32px 48px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📅</div>
            <div style={{ color: "#c7d2fe", fontSize: 15 }}>Loading your calendar events…</div>
          </div>
        </div>
      )}

      {showModal && <CalendarModal onClose={() => setShowModal(false)} onConnect={(p) => { setConnected(p); notify(`Connected to ${p === "microsoft" ? "Microsoft" : "Google"} Calendar`); }} />}
      {showSettings && <SettingsModal apiKey={apiKey} onSave={saveApiKey} onClose={() => setShowSettings(false)} />}
      {showCalPicker && calendarList.length > 0 && <CalendarPickerModal calendars={calendarList} selected={selectedCalendars} onApply={applyCalendarSelection} onClose={() => setShowCalPicker(false)} />}

      <EMRShell calendarPage={calendarPage} transcribePage={transcribePage} session={session} practiceId={practiceId} />
    </>
  );
}
